import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { CommonService } from '../../common-service';
import { EtapasKanbanRequest } from '../../requests/compras/etapas-kanban.request';
import { CreateEtapasKanbanResponse } from '../../responses/compras/create-etapas-kanban.response';
import { DeleteEtapasKanbanResponse } from '../../responses/compras/delete-etapas-kanban.response';
import { GetEtapasKanbanResponse } from '../../responses/compras/get-etapas-kanban.response';
import { GetNiveisDadosCompraResponse } from '../../responses/compras/get-niveis-dados-compra.response';
import { BulkInativarEtapasKanbanResponse } from '../../responses/compras/bulk-inativar-etapas-kanban.response';
import { ListarMovimentacoesEtapaResponse } from '../../responses/compras/listar-movimentacoes-etapa.response';
import { ConfiguracaoEtapaCompletaRequest } from '../../requests/compras/configuracao-etapa-completa.request';
import { GetCamposNativosEtapaResponse } from '../../responses/compras/get-campos-nativos-etapa.response';
import { GetConfiguracaoEtapaResponse } from '../../responses/compras/get-configuracao-etapa.response';
import { GetPerfisNotificacaoAprovacaoResponse } from '../../responses/compras/get-perfis-notificacao-aprovacao.response';
import { GetRegrasAprovacaoResponse } from '../../responses/compras/get-regras-aprovacao.response';
import { SalvarConfiguracaoEtapaResponse } from '../../responses/compras/salvar-configuracao-etapa.response';
import { PerfilUsuarioVinculoRequest } from '../../requests/compras/perfil-usuario-vinculo.request';
import { PerfilService } from '../usuario/perfil.service';

@Injectable({
  providedIn: 'root',
})
export class EtapasKanbanService extends CommonService {
  private readonly apiUrl: string = environment.urlApiCompras + 'EtapasKanban';

  /**
   * Cache dos destinatários do aviso de aprovação: o quadro consulta a cada card movido para
   * aprovação e a configuração de alçada muda raramente. `salvarConfiguracaoCompleta` invalida.
   */
  private vinculosAprovadores$?: Observable<PerfilUsuarioVinculoRequest[]>;

  constructor(
    private httpClient: HttpClient,
    private perfilService: PerfilService,
  ) {
    super();
  }

  public getAll(nome: string = '', incluirContadores: boolean = true): Observable<GetEtapasKanbanResponse> {
    let params = new HttpParams();

    if (nome) {
      params = params.set('nome', nome);
    }

    params = params.set('incluirContadores', String(incluirContadores));

    return this.httpClient
      .get<GetEtapasKanbanResponse>(this.apiUrl, { params, ...this.addHeaderToken() })
      .pipe(catchError(this.handleError));
  }

  public listarMovimentacoes(): Observable<ListarMovimentacoesEtapaResponse> {
    const url = `${this.apiUrl}/Movimentacoes`;
    return this.httpClient
      .get<ListarMovimentacoesEtapaResponse>(url, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** Lookup nivel_dados_compra — alimenta os selects de nível e o motivo de destino bloqueado. */
  public getNiveisDados(): Observable<GetNiveisDadosCompraResponse> {
    const url = `${this.apiUrl}/niveis-dados`;
    return this.httpClient
      .get<GetNiveisDadosCompraResponse>(url, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public create(request: EtapasKanbanRequest): Observable<CreateEtapasKanbanResponse> {
    return this.httpClient
      .post<CreateEtapasKanbanResponse>(this.apiUrl, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public update(id: number, request: EtapasKanbanRequest): Observable<CreateEtapasKanbanResponse> {
    const url = `${this.apiUrl}/idEtapaKanban/${id}`;
    return this.httpClient
      .put<CreateEtapasKanbanResponse>(url, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public inativar(id: number): Observable<BulkInativarEtapasKanbanResponse> {
    const url = `${this.apiUrl}/inativar/${id}`;
    return this.httpClient
      .put<BulkInativarEtapasKanbanResponse>(url, {}, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public delete(id: number): Observable<DeleteEtapasKanbanResponse> {
    const url = `${this.apiUrl}/idEtapaKanban/${id}`;
    return this.httpClient
      .delete<DeleteEtapasKanbanResponse>(url, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  // ---- Sub-configurações da etapa ----
  public getConfiguracao(id: number): Observable<GetConfiguracaoEtapaResponse> {
    const url = `${this.apiUrl}/idEtapaKanban/${id}/configuracao`;
    return this.httpClient
      .get<GetConfiguracaoEtapaResponse>(url, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  // Endpoint enxuto para o quadro: só os campos nativos da etapa (sem
  // movimentações/permissões), liberado para quem visualiza o processo de compras.
  public getCamposNativos(id: number): Observable<GetCamposNativosEtapaResponse> {
    const url = `${this.apiUrl}/idEtapaKanban/${id}/campos-nativos`;
    return this.httpClient
      .get<GetCamposNativosEtapaResponse>(url, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  // Salvamento composto: a etapa e todas as sub-configurações em uma transação no backend.
  public salvarConfiguracaoCompleta(id: number, request: ConfiguracaoEtapaCompletaRequest): Observable<SalvarConfiguracaoEtapaResponse> {
    const url = `${this.apiUrl}/idEtapaKanban/${id}/configuracao`;
    return this.httpClient
      .put<SalvarConfiguracaoEtapaResponse>(url, request, this.addHeaderToken())
      .pipe(
        // As faixas vão neste payload: depois de gravadas, o cache de destinatários do aviso está velho.
        // Invalidar antes do PUT deixava uma leitura concorrente repovoar o cache com as faixas antigas.
        tap(response => {
          if (!response.isError) this.invalidarCacheAprovadores();
        }),
        catchError(this.handleError),
      );
  }

  // ---- Condições de aprovação (por empresa) ----
  public getRegrasAprovacao(): Observable<GetRegrasAprovacaoResponse> {
    const url = `${this.apiUrl}/regras-aprovacao`;
    return this.httpClient
      .get<GetRegrasAprovacaoResponse>(url, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }


  /**
   * Expande em vínculos perfil -> usuário os perfis liberadores das faixas com aviso ligado. Quem
   * escolhe a faixa aplicável é o backend; a tela só resolve o que depende da API de Usuário.
   *
   * Os perfis vêm de um endpoint sob a permissão do processo de compras: o de regras exige acesso
   * à configuração, que quem movimenta card normalmente não tem — e o aviso morreria calado.
   *
   * Sem faixa com aviso, resolve vazio sem chamar ninguém.
   */
  public vinculosPerfilAprovadores(): Observable<PerfilUsuarioVinculoRequest[]> {
    if (!this.vinculosAprovadores$) {
      this.vinculosAprovadores$ = this.getPerfisNotificacaoAprovacao().pipe(
        switchMap(res => {
          if (res.isError) throw new Error(res.errorDescription || 'Não foi possível carregar os perfis notificados.');

          const idsPerfil = res.idsPerfil || [];
          if (!idsPerfil.length) return of([] as PerfilUsuarioVinculoRequest[]);

          return this.perfilService.getUsuariosPorPerfil(idsPerfil).pipe(
            map(perfis => {
              if (perfis.isError) throw new Error(perfis.errorDescription || 'Não foi possível expandir os perfis.');
              return (perfis.lista || []).map(v => ({ idPerfil: v.idPerfil, idUsuario: v.idUsuario }));
            }),
          );
        }),
        // Falha resolve vazio só para quem esperava, e solta o cache: memorizar "ninguém para
        // notificar" por causa de uma falha transitória silenciaria o aviso pela sessão inteira.
        catchError(() => {
          this.invalidarCacheAprovadores();
          return of([] as PerfilUsuarioVinculoRequest[]);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }

    return this.vinculosAprovadores$;
  }

  public getPerfisNotificacaoAprovacao(): Observable<GetPerfisNotificacaoAprovacaoResponse> {
    const url = `${this.apiUrl}/regras-aprovacao/perfis-notificados`;
    return this.httpClient
      .get<GetPerfisNotificacaoAprovacaoResponse>(url, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  private invalidarCacheAprovadores(): void {
    this.vinculosAprovadores$ = undefined;
  }

}
